import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useSnackbar } from 'notistack';
import axios from "utils/axios";
import { useSelector } from 'react-redux';
import QuillEditorComponent from '../QuillEditorComponent';

const LastCall = ({ closeModal, vendors, rfqId, entityId, rfqData }) => {
    const auth = useSelector((state) => state.auth);
    const user = auth?.user;
    const { enqueueSnackbar } = useSnackbar();
    const [closureDate, setClosureDate] = useState('');
    const [defaultMessage, setDefaultMessage] = useState('');
    const [vendorSelectionError, setVendorSelectionError] = useState('');

    const formatClosureDate = (date) => {
        if (!date) return '';

        const [year, month, day] = date.split('-');
        if (!year || !month || !day) return date;

        return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'Asia/Kolkata',
        }).format(new Date(`${year}-${month}-${day}T00:00:00+05:30`));
    };

    const getMessageTemplate = (closureDate) => (
        `This is to inform you that the RFQ for <b>${rfqData.projectDetails ? rfqData?.projectDetails.name : rfqData?.projectName || ""}</b>, <b>${rfqData?.location}</b> below will be closing soon.<br />
        Closure Date : <b>${closureDate ? `${formatClosureDate(closureDate)}` : ''}</b><br />
        If you haven't shared your quotation yet, please submit it as soon as possible.`
    );

    useEffect(() => {
        setDefaultMessage(getMessageTemplate(closureDate));
    }, [closureDate, rfqData, user]);

    const allVendor = { businessName: 'All', supplierId: 'all' };
    const vendorList = vendors.length > 1 ? [allVendor, ...vendors] : vendors;

    const [isAllSelected, setIsAllSelected] = useState(false);
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleCheckboxChange = (vendor) => {
        if (vendor.supplierId === 'all') {
            setIsAllSelected(!isAllSelected);
            setSelectedVendors(!isAllSelected ? vendors.map(v => v.supplierId) : []);
            setVendorSelectionError('');
        } else {
            const updatedVendors = selectedVendors.includes(vendor.supplierId)
                ? selectedVendors.filter(id => id !== vendor.supplierId)
                : [...selectedVendors, vendor.supplierId];

            setSelectedVendors(updatedVendors);
            setIsAllSelected(updatedVendors.length === vendors.length);
            setVendorSelectionError('');
        }
    };

    const handleNotifyVendors = async (values) => {
        if (!closureDate) {
            enqueueSnackbar('Please select a target closure date.', { variant: 'warning', autoHideDuration: 3000 });
            return;
        }

        if (selectedVendors.length === 0) {
            setVendorSelectionError('Please select at least one vendor.');
            enqueueSnackbar('Please select at least one vendor to notify.', { variant: 'warning', autoHideDuration: 3000 });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                '/api/rfqs/last-call',
                { rfqId, entityId, closureDate, supplierIds: selectedVendors, message: values.message },
                { headers: { 'Content-Type': 'application/json' } }
            );

            enqueueSnackbar('Vendors notified successfully', { variant: 'success', autoHideDuration: 3000 });
            setSelectedVendors([]);
            setClosureDate('');
            setVendorSelectionError('');
            closeModal();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'An error occurred. Please try again.', { variant: 'error', autoHideDuration: 3000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Formik
            initialValues={{ message: defaultMessage }}
            onSubmit={handleNotifyVendors}
            enableReinitialize // Ensures message updates in Formik when defaultMessage changes
        >
            {({ values, setFieldValue }) => (
                <Form>
                    <div className="relative flex h-[calc(100vh-180px)] max-h-[calc(100vh-180px)] min-h-0 flex-col overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_12px_30px_rgba(15,23,42,0.1)]">
                        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                            <div className="mb-5">
                                <label className="mb-2 block text-left text-sm font-bold text-[#374151]">
                                    Target Closure Date<span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={closureDate}
                                    onChange={(e) => setClosureDate(e.target.value)}
                                    required
                                    onClick={(event) => event.currentTarget.showPicker?.()}
                                    className="w-full cursor-pointer rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-semibold text-[#374151] outline-none transition focus:border-[#FD6600] focus:ring-2 focus:ring-[#FD6600]/15"
                                />
                            </div>
                            <div className="mb-5">
                                <label className="mb-3 block text-left text-sm font-bold text-[#374151]">
                                    Select all the vendors who should be notified<span className="text-red-500">*</span>
                                </label>
                                <div className="grid gap-2 sm:grid-cols-2">
                                        {vendorList.map((vendor, index) => (
                                            <label key={vendor.supplierId} htmlFor={`vendor-${index}`} className="flex cursor-pointer items-center rounded-md border border-[#E5E7EB] bg-[#FAFAF9] px-3 py-2 transition hover:border-[#FDBA74] hover:bg-[#FFF7ED]">
                                                <input
                                                    type="checkbox"
                                                    id={`vendor-${index}`}
                                                    value={vendor.supplierId}
                                                    checked={vendor.supplierId === 'all' ? isAllSelected : selectedVendors.includes(vendor.supplierId)}
                                                    onChange={() => handleCheckboxChange(vendor)}
                                                    className="mr-2 h-4 w-4 rounded border-[#CBD5E1] text-[#FD6600] focus:ring-[#FD6600]"
                                                />
                                                <span className="text-xs font-semibold text-[#4B5563]">
                                                    {vendor.businessName}
                                                </span>
                                            </label>
                                        ))}
                                </div>
                                {vendorSelectionError && (
                                    <p className="mt-2 text-xs font-medium text-red-600">{vendorSelectionError}</p>
                                )}
                            </div>
                            <div className="mb-1">
                                <label className="mb-3 block text-left text-sm font-bold text-[#374151]">
                                    Please write the message to send to the vendors
                                </label>
                                <div className="overflow-hidden rounded-md border border-[#D1D5DB] bg-white focus-within:border-[#FD6600] focus-within:ring-2 focus-within:ring-[#FD6600]/15">
                                    <QuillEditorComponent
                                        fieldName="message"
                                        placeholder="Please write your message here (Rich Text)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex shrink-0 justify-end gap-3 border-t border-[#E5E7EB] bg-[#FAFAF9] px-6 py-3">
                            <button
                                onClick={closeModal}
                                className="rounded-md border border-[#CBD5E1] bg-white px-4 py-2 text-xs font-bold text-[#4B5563] transition hover:border-[#FDBA74] hover:bg-[#FFF7ED]"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="rounded-md bg-[#FD6600] px-5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-[#E85B00] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading ? 'Loading...' : 'Notify Vendors'}
                            </button>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default LastCall;
